import { Component, OnDestroy, ViewEncapsulation } from '@angular/core';
import { Subscription } from 'rxjs';
import { MatRippleModule } from '@angular/material/core';
import { MatIconModule } from '@angular/material/icon';
import { NgFor, NgIf } from '@angular/common';
import { TranslateModule } from '@ngx-translate/core';
import { MatTooltipModule } from '@angular/material/tooltip';

import { WEIGHT_OPTIONS } from '../../../../shared/models/list';
import { GROSS } from '../../../../shared/models/settings';
import { User } from '../../../../shared/models/user';
import { UserService } from '../../../../shared/services/user.service';
import { UNDERSCORE } from '../../../../shared/utils/constant';
import { CalledBatch, Ecalls, GroupCalledBatch } from '../../../models/ecall-off-model';
import { ECallOffService } from '../../../services/ecall-off.service';
import { Batch } from '../../../../order/models/batch';
import { ShipToDetails } from '../../../../order/models/ship-to-details';
import { NumberPipe } from '../../../../shared/pipes/number.pipe';
import { DatePipe } from '../../../../shared/pipes/date.pipe';
import { EcallOffHeaderComponent } from '../../header/ecall-off-header.component';

@Component({
	selector: 'copo-step-summary',
	templateUrl: './step-summary.component.html',
	styleUrl: './step-summary.component.css',
	encapsulation: ViewEncapsulation.Emulated,
	imports: [
		EcallOffHeaderComponent,
		TranslateModule,
		NgFor,
		MatIconModule,
		MatTooltipModule,
		NgIf,
		MatRippleModule,
		DatePipe,
		NumberPipe
	]
})

export class StepSummaryComponent implements OnDestroy {
	readonly UNDERSCORE = UNDERSCORE;
	canShowComments = false;
	custCallOffRef?: string;
	rddMode?: string;
	copyTo?: string;
	isReqDateVisible = true;
	fromTo = false;
	calledBatches?: CalledBatch[];
	user?: User;
	weightDisplay: keyof typeof WEIGHT_OPTIONS = GROSS;
	requestBody?: Ecalls;
	sub?: Subscription;
	batchesAllData?: Batch[];
	groupCalledBatch: GroupCalledBatch[] = [];
	ecallData!: Ecalls;
	isFromToDateVisible = false;
	isisReqDateVisibleVisible = false;
	showComments = false;

	shipToDetails?: ShipToDetails[];
	displayShipTo = false;
	constructor(
		private readonly ecallOffService: ECallOffService,
		private readonly userService: UserService
	) {
		this.sub = this.ecallOffService.currentselectedBatchesStep2.subscribe(batches => {
			this.calledBatches = batches;
			this.canShowComments = this.calledBatches.some(batch => batch.comment);
		});
		this.sub = this.ecallOffService.currentCustCallOffRef
			.subscribe(custCallOffRef => {
				this.custCallOffRef = custCallOffRef;
			});
		this.sub = this.ecallOffService.currentCopyTo
			.subscribe(copyTo => {
				this.copyTo = copyTo;
			});
		this.sub = this.ecallOffService.currentRddMode
			.subscribe(rddMode => {
				this.rddMode = rddMode;
			});
		this.sub = this.ecallOffService.shipToList
			.subscribe(shipTos => {
				this.shipToDetails = shipTos;
				if (this.shipToDetails.length > 0) {
					this.displayShipTo = true;
				}
			});
		this.user = this.userService.user;
		switch (this.rddMode) {
		case 'ASAP':
			this.isReqDateVisible = false;
			this.fromTo = false;
			break;
		case 'BETWEEN':
			this.isReqDateVisible = false;
			this.fromTo = true;
			this.isFromToDateVisible = true;
			break;
		case 'LATEST':
		case 'EARLIEST':
			this.isReqDateVisible = true;
			this.fromTo = false;
			break;
		default:
			this.isReqDateVisible = true;
			this.fromTo = false;
			this.isisReqDateVisibleVisible = true;
			break;
		}
		this.getEcallDetails();
	}

	getTonnage() {
		if (!this.calledBatches) {
			return 0;
		}

		let sum = 0;
		for (const batch of this.calledBatches) {
			sum += this.weightDisplay === 'net'
				? Number(batch.netWeight)
				: Number(batch.grossWeight);
		}
		return sum;
	}

	getWeight(calledBatch: CalledBatch) {
		return this.weightDisplay === 'net' ? calledBatch.netWeight : calledBatch.grossWeight;
	}

	ngOnDestroy() {
		if (this.sub !== undefined) {
			this.sub.unsubscribe();
		}
	}

	getTooltipText(tooltipText: string) {
		if (tooltipText && tooltipText.length > 25) {
			const textArray = tooltipText.match(/.{1,25}/g);
			return textArray ? textArray.join('\n') : tooltipText;
		}
		return tooltipText;
	}

	getEcallDetails() {
		if (this.calledBatches) {
			const groupedKeys = this.calledBatches.reduce((group: Record<string, CalledBatch[]>, item) => {
				if (!Object.hasOwn(group, item.millDescription)) {
					group[item.millDescription] = [];
				}
				group[item.millDescription].push(item);
				return group;
			}, {});
			const groupKeys = Object.keys(groupedKeys);
			for (const element of groupKeys) {
				this.groupCalledBatch.push({
					millDescription: element,
					millCodeGroup: groupedKeys[element],
					grossWeight: this.getTotalGrossWeightBatches(groupedKeys[element]),
					netWeight: this.getTotalNetWeightBatches(groupedKeys[element])
				});
			}
		}
	}

	getTotalGrossWeightBatches(batches: CalledBatch[]) {
		return batches.reduce((sum, batchWeight) => sum + Number(batchWeight.grossWeight || 0), 0);
	}

	getTotalNetWeightBatches(batches: CalledBatch[]) {
		return batches.reduce((sum, batchWeight) => sum + Number(batchWeight.netWeight || 0), 0);
	}
}
